import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear, faUser, faBuilding, faUsers, faPlus,
  faTrash, faShield, faCrown,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { FormField } from '../components/common/FormField';
import { Modal } from '../components/common/Modal';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { org as orgApi, team as teamApi, notifications as notifApi } from '../api';
import type { User, Organization, Notification as NotifType } from '../types';

type SettingsTab = 'profile' | 'organization' | 'team' | 'notifications';

const ROLE_ICONS: Record<string, typeof faCrown> = { owner: faCrown, admin: faShield, member: faUser, viewer: faUser };
const ROLE_COLORS: Record<string, string> = { owner: 'var(--color-warning)', admin: 'var(--color-accent)', member: 'var(--color-info)', viewer: 'var(--color-text-tertiary)' };

export function Settings() {
  const { user, logout } = useAuth();
  const { addToast } = useToast();
  const [tab, setTab] = useState<SettingsTab>('profile');
  const [organization, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [notifs, setNotifs] = useState<NotifType[]>([]);
  const [unread, setUnread] = useState(0);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' });
  const [orgForm, setOrgForm] = useState({ name: '' });

  const loadOrg = useCallback(async () => {
    try { const o = await orgApi.get(); setOrg(o); setOrgForm({ name: o.name }); } catch {}
  }, []);

  const loadTeam = useCallback(async () => {
    try { setMembers(await teamApi.list()); } catch {}
  }, []);

  const loadNotifs = useCallback(async () => {
    try { const r = await notifApi.list(); setNotifs(r.notifications); setUnread(r.unread_count); } catch {}
  }, []);

  useEffect(() => { loadOrg(); loadTeam(); loadNotifs(); }, [loadOrg, loadTeam, loadNotifs]);

  const handleUpdateOrg = async () => {
    try { await orgApi.update({ name: orgForm.name } as Partial<Organization>); addToast('success', '조직 정보가 업데이트되었습니다'); loadOrg(); }
    catch { addToast('error', '업데이트 실패'); }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    try {
      await teamApi.invite(inviteForm);
      addToast('success', `${inviteForm.email} 초대됨`);
      setShowInvite(false);
      setInviteForm({ email: '', name: '', role: 'member' });
      loadTeam();
    } catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
  };

  const handleRemoveMember = async (userId: number) => {
    try { await teamApi.remove(userId); addToast('success', '멤버가 제거되었습니다'); loadTeam(); }
    catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
  };

  const handleReadAll = async () => {
    try { await notifApi.readAll(); setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, is_read: 1 }))); }
    catch {}
  };

  const TABS: { key: SettingsTab; label: string; icon: typeof faGear }[] = [
    { key: 'profile', label: '프로필', icon: faUser },
    { key: 'organization', label: '조직', icon: faBuilding },
    { key: 'team', label: '팀', icon: faUsers },
    { key: 'notifications', label: '알림', icon: faBell },
  ];

  return (
    <main id="main-content" role="main" className="container" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      <FadeUp>
        <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-8)' }}>
          <FontAwesomeIcon icon={faGear} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-3)' }} />
          설정
        </h1>
      </FadeUp>

      <div className="settings-layout" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-6)' }}>
        {/* Nav */}
        <FadeUp delay={0.05}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', overflowX: 'auto', paddingBottom: 'var(--space-2)' }}>
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  fontWeight: 500, fontSize: 'var(--text-sm)',
                  background: tab === t.key ? 'var(--color-accent-subtle)' : 'transparent',
                  color: tab === t.key ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                }}
              >
                <FontAwesomeIcon icon={t.icon} />
                {t.label}
                {t.key === 'notifications' && unread > 0 && (
                  <Badge color="var(--color-error)" variant="solid">{unread}</Badge>
                )}
              </button>
            ))}
          </div>
        </FadeUp>

        {/* Content */}
        <FadeUp delay={0.1}>
          {tab === 'profile' && user && (
            <GlassCard>
              <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-6)', fontFamily: 'var(--font-display)' }}>프로필</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
                <div style={{
                  width: '4rem', height: '4rem', borderRadius: 'var(--radius-full)',
                  background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, color: 'var(--color-accent)', fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)',
                }}>
                  {user.name[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>{user.name}</div>
                  <div style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>{user.email}</div>
                  <Badge color={ROLE_COLORS[user.role]}><FontAwesomeIcon icon={ROLE_ICONS[user.role]} /> {user.role}</Badge>
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--space-6)' }}>
                <Button variant="ghost" onClick={logout} style={{ color: 'var(--color-error)' }}>로그아웃</Button>
              </div>
            </GlassCard>
          )}

          {tab === 'organization' && organization && (
            <GlassCard>
              <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-6)', fontFamily: 'var(--font-display)' }}>조직</h3>
              <FormField label="이름" value={orgForm.name} onChange={e => setOrgForm({ name: (e.target as HTMLInputElement).value })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
                <Badge color="var(--color-accent)" variant="solid">{organization.plan.toUpperCase()}</Badge>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                  {organization.plan_seats}석 &middot; {organization.plan_storage_gb} GB 저장공간
                </span>
              </div>
              <Button variant="primary" onClick={handleUpdateOrg}>변경사항 저장</Button>
            </GlassCard>
          )}

          {tab === 'team' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                <h3 style={{ fontSize: 'var(--text-lg)', fontFamily: 'var(--font-display)' }}>팀 멤버 ({members.length})</h3>
                <Button variant="primary" size="sm" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowInvite(true)}>초대</Button>
              </div>
              <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                {members.map(m => (
                  <GlassCard key={m.id} style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <div style={{
                        width: '2.5rem', height: '2.5rem', borderRadius: 'var(--radius-full)', flexShrink: 0,
                        background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, color: 'var(--color-accent)', fontSize: 'var(--text-sm)',
                      }}>
                        {(m.name || m.email)[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{m.name || m.email}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{m.email}</div>
                      </div>
                      <Badge color={ROLE_COLORS[m.role]}><FontAwesomeIcon icon={ROLE_ICONS[m.role]} /> {m.role}</Badge>
                      {user && m.id !== user.id && (
                        <button onClick={() => handleRemoveMember(m.id)} aria-label={`${m.name} 제거`} style={{ background: 'none', border: 'none', color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          )}

          {tab === 'notifications' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                <h3 style={{ fontSize: 'var(--text-lg)', fontFamily: 'var(--font-display)' }}>알림</h3>
                {unread > 0 && <Button variant="ghost" size="sm" onClick={handleReadAll}>모두 읽음 처리</Button>}
              </div>
              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                {notifs.map(n => (
                  <GlassCard key={n.id} style={{ padding: 'var(--space-3) var(--space-4)', opacity: n.is_read ? 0.7 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {!n.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-accent)', flexShrink: 0 }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize: 'var(--text-sm)' }}>{n.title}</div>
                        {n.body && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{n.body}</div>}
                      </div>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', flexShrink: 0 }}>
                        {new Date(n.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </GlassCard>
                ))}
                {notifs.length === 0 && <GlassCard style={{ textAlign: 'center', padding: 'var(--space-10)' }}><p style={{ color: 'var(--color-text-tertiary)' }}>알림이 없습니다</p></GlassCard>}
              </div>
            </div>
          )}
        </FadeUp>
      </div>

      {/* Invite Modal */}
      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="팀 멤버 초대" footer={
        <><Button variant="ghost" onClick={() => setShowInvite(false)}>취소</Button><Button variant="primary" onClick={handleInvite}>초대 보내기</Button></>
      }>
        <form onSubmit={handleInvite}>
          <FormField label="이메일" required type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: (e.target as HTMLInputElement).value }))} />
          <FormField label="이름" value={inviteForm.name} onChange={e => setInviteForm(p => ({ ...p, name: (e.target as HTMLInputElement).value }))} />
          <FormField label="역할" as="select" value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: (e.target as HTMLSelectElement).value }))}>
            <option value="member">멤버</option><option value="admin">관리자</option><option value="viewer">뷰어</option>
          </FormField>
        </form>
      </Modal>

      <style>{`
        @media (min-width: 768px) { .settings-layout { grid-template-columns: 1fr !important; } }
      `}</style>
    </main>
  );
}
